import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { listEntities, getEntity, createEntity, updateEntity } from '../api/client';
import { EntityCard, FilterBar, EmptyState, SignalCard } from '../components';
import type { Entity, FilterConfig, Signal } from '../components';

const ENTITY_TYPES = ['order', 'delivery_update', 'supplier_issue', 'production_task', 'payment_request', 'general_followup'] as const;
const LIFECYCLE_STATES = ['detected', 'confirmed', 'scheduled', 'in_production', 'ready', 'delivered', 'completed', 'cancelled'] as const;

interface EntityDetail extends Entity {
  description?: string;
  quantity?: number;
  linked_signals?: Signal[];
  timeline?: { timestamp: string; action: string; details?: string }[];
}

export default function Entities() {
  const { t } = useTranslation();
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<EntityDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<EntityDetail>>({});
  const [createForm, setCreateForm] = useState({ title: '', entity_type: 'order', description: '', customer_name: '', supplier_name: '', due_date: '' });

  const filters: FilterConfig[] = [
    { key: 'entity_type', label: t('common.filter') + ' Type', options: ENTITY_TYPES.map((v) => ({ value: v, label: t(`entities.types.${v}`) })) },
    { key: 'lifecycle_state', label: 'State', options: LIFECYCLE_STATES.map((v) => ({ value: v, label: t(`entities.lifecycle.${v}`) })) },
  ];

  const fetchEntities = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      for (const [k, v] of Object.entries(activeFilters)) { if (v) params[k] = v; }
      const res = await listEntities(params);
      setEntities(res.data?.entities ?? res.data ?? []);
    } catch {
      setEntities([]);
    } finally {
      setLoading(false);
    }
  }, [activeFilters]);

  useEffect(() => { fetchEntities(); }, [fetchEntities]);

  const fetchDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    try {
      const res = await getEntity(id);
      setDetail(res.data);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) fetchDetail(selectedId);
    else setDetail(null);
  }, [selectedId, fetchDetail]);

  const handleFilterChange = (key: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleCreate = async () => {
    if (!createForm.title) return;
    const data: Record<string, string | undefined> = { ...createForm };
    if (!data.due_date) delete data.due_date;
    if (!data.customer_name) delete data.customer_name;
    if (!data.supplier_name) delete data.supplier_name;
    await createEntity(data);
    setCreateForm({ title: '', entity_type: 'order', description: '', customer_name: '', supplier_name: '', due_date: '' });
    setShowCreate(false);
    fetchEntities();
  };

  const handleSaveEdit = async () => {
    if (!selectedId) return;
    await updateEntity(selectedId, editForm);
    setEditing(false);
    fetchDetail(selectedId);
    fetchEntities();
  };

  const startEdit = () => {
    if (!detail) return;
    setEditForm({ title: detail.title, description: detail.description, lifecycle_state: detail.lifecycle_state, customer_name: detail.customer_name, supplier_name: detail.supplier_name, due_date: detail.due_date });
    setEditing(true);
  };

  // Lifecycle progress bar
  const LifecycleProgress = ({ state }: { state: string }) => {
    const idx = LIFECYCLE_STATES.indexOf(state as typeof LIFECYCLE_STATES[number]);
    const pct = idx >= 0 ? Math.round(((idx + 1) / LIFECYCLE_STATES.length) * 100) : 0;
    return (
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-gray-500">
          <span>{t(`entities.lifecycle.${state}`)}</span>
          <span>{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-gray-200">
          <div className="h-2 rounded-full bg-primary-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex justify-between text-[10px] text-gray-400">
          <span>{t('entities.lifecycle.detected')}</span>
          <span>{t('entities.lifecycle.completed')}</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[40vh] text-gray-400">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('entities.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('entities.subtitle')}</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 transition-colors">
          + Create Entity
        </button>
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
        onClear={() => setActiveFilters({})}
      />

      {/* Entity grid + detail panel */}
      {entities.length === 0 ? (
        <EmptyState icon="📦" title={t('entities.title')} description={t('entities.emptyState')} action={{ label: 'Create Entity', onClick: () => setShowCreate(true) }} />
      ) : (
        <div className={`flex gap-6 ${selectedId ? '' : ''}`}>
          {/* Grid */}
          <div className={`grid gap-4 ${selectedId ? 'grid-cols-1 md:grid-cols-1 w-full md:w-1/2 lg:w-2/5' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 w-full'} transition-all`}>
            {entities.map((entity) => (
              <EntityCard
                key={entity.id}
                entity={entity}
                onViewDetail={(id) => setSelectedId(selectedId === id ? null : id)}
                compact={!!selectedId}
              />
            ))}
          </div>

          {/* Detail panel */}
          {selectedId && (
            <div className="hidden md:block w-1/2 lg:w-3/5">
              <div className="sticky top-20 rounded-lg border border-gray-200 bg-white shadow-sm p-5 space-y-5 max-h-[calc(100vh-8rem)] overflow-y-auto">
                {detailLoading ? (
                  <div className="text-center text-gray-400 py-8">{t('common.loading')}</div>
                ) : detail ? (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        {editing ? (
                          <input value={editForm.title ?? ''} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="text-lg font-bold text-gray-900 border-b border-primary-400 outline-none w-full" />
                        ) : (
                          <h2 className="text-lg font-bold text-gray-900">{detail.title}</h2>
                        )}
                        <span className="text-xs text-gray-500">{t(`entities.types.${detail.entity_type}`)}</span>
                      </div>
                      <div className="flex gap-2">
                        {editing ? (
                          <>
                            <button onClick={handleSaveEdit} className="rounded-md bg-green-50 px-3 py-1 text-xs font-medium text-green-700 hover:bg-green-100">{t('common.save')}</button>
                            <button onClick={() => setEditing(false)} className="rounded-md bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100">{t('common.cancel')}</button>
                          </>
                        ) : (
                          <>
                            <button onClick={startEdit} className="rounded-md bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100">✏️ {t('common.edit')}</button>
                            <button onClick={() => setSelectedId(null)} className="rounded-md bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100">&times;</button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Lifecycle progress */}
                    <LifecycleProgress state={editing ? (editForm.lifecycle_state ?? detail.lifecycle_state) : detail.lifecycle_state} />

                    {editing ? (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
                          <select value={editForm.lifecycle_state ?? ''} onChange={(e) => setEditForm({ ...editForm, lifecycle_state: e.target.value })} className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm">
                            {LIFECYCLE_STATES.map((s) => <option key={s} value={s}>{t(`entities.lifecycle.${s}`)}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                          <textarea value={editForm.description ?? ''} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={3} className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm resize-none" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Customer</label>
                            <input value={editForm.customer_name ?? ''} onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })} className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Supplier</label>
                            <input value={editForm.supplier_name ?? ''} onChange={(e) => setEditForm({ ...editForm, supplier_name: e.target.value })} className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Due Date</label>
                          <input type="date" value={editForm.due_date ?? ''} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm" />
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Details */}
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {detail.customer_name && <div><span className="text-xs text-gray-500">Customer</span><p className="font-medium">{detail.customer_name}</p></div>}
                          {detail.supplier_name && <div><span className="text-xs text-gray-500">Supplier</span><p className="font-medium">{detail.supplier_name}</p></div>}
                          {detail.products && detail.products.length > 0 && <div className="col-span-2"><span className="text-xs text-gray-500">Products</span><p className="font-medium">{detail.products.join(', ')}</p></div>}
                          {detail.due_date && <div><span className="text-xs text-gray-500">Due Date</span><p className="font-medium">{new Date(detail.due_date).toLocaleDateString()}</p></div>}
                          {detail.quantity != null && <div><span className="text-xs text-gray-500">Quantity</span><p className="font-medium">{detail.quantity}</p></div>}
                        </div>
                        {detail.description && <p className="text-sm text-gray-600">{detail.description}</p>}

                        {/* Linked signals */}
                        {detail.linked_signals && detail.linked_signals.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">📡 {t('entities.linkedSignals')}</h4>
                            <div className="space-y-2">
                              {detail.linked_signals.map((sig) => (
                                <SignalCard key={sig.id} signal={sig} compact onConfirm={() => {}} onDismiss={() => {}} onExpand={() => {}} />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Timeline */}
                        {detail.timeline && detail.timeline.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">📋 {t('entities.timeline')}</h4>
                            <div className="space-y-2 border-l-2 border-gray-200 pl-4">
                              {detail.timeline.map((event, i) => (
                                <div key={i} className="relative">
                                  <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary-500 border-2 border-white" />
                                  <p className="text-sm font-medium text-gray-900">{event.action}</p>
                                  {event.details && <p className="text-xs text-gray-500">{event.details}</p>}
                                  <time className="text-[10px] text-gray-400">{new Date(event.timestamp).toLocaleString()}</time>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <div className="text-center text-gray-400 py-8">Entity not found</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Entity modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Entity</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input type="text" value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={createForm.entity_type} onChange={(e) => setCreateForm({ ...createForm, entity_type: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                  {ENTITY_TYPES.map((et) => <option key={et} value={et}>{t(`entities.types.${et}`)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} rows={3} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                  <input type="text" value={createForm.customer_name} onChange={(e) => setCreateForm({ ...createForm, customer_name: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                  <input type="text" value={createForm.supplier_name} onChange={(e) => setCreateForm({ ...createForm, supplier_name: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input type="date" value={createForm.due_date} onChange={(e) => setCreateForm({ ...createForm, due_date: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowCreate(false)} className="rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-100">{t('common.cancel')}</button>
              <button onClick={handleCreate} disabled={!createForm.title} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
